import { localize } from "render/context/InternationalizationContext";

const Title = localize({
  en_GB: "Your participation in our clinical studies",
  sv_SE: "Ditt deltagande i våra kliniska studier",
});

const Summary = localize({
  en_GB:
    "Research is in our DNA, and many data points that we collect during the visit are used in clinical trials to challenge current hypotheses and foster innovation. The trials we create are funded by us and conducted by our in-house doctors in collaboration with other world-renowned clinicians and institutions.",
  sv_SE:
    "På Neko Health vill vi tänja på gränserna för teknik och vetenskap för att skapa bästa möjliga tjänst så att våra medlemmar kan ligga steget före sin hälsa. Forskning finns i vårt DNA och mycket av den data vi samlar in under ditt besök används i kliniska studier för att främja innovation. Studierna är självfinansierade och genomförs av våra läkare i samarbete med andra ledande kliniker och institutioner.",
});

const HowItWorks = localize({
  en_GB: "Our studies",
  sv_SE: "Våra studier",
});

const StudyExplanation = localize({
  en_GB: (
    <>
      <p>
        All the trials we conduct have been reviewed by The Swedish Medical
        Products Agency (Läkemedelsverket) and the Swedish Ethical Review
        Authority (Etikprövningsmyndigheten).
      </p>
      <p>
        It is always voluntary to participate in our studies, but a requirement
        if you want to get a scan.
      </p>
    </>
  ),
  sv_SE: (
    <>
      <p>
        Studierna har granskats och godkänts av Läkemedelsverket och
        Etikprövningsmyndigheten.
      </p>
      <p>
        Det är alltid frivilligt att delta i våra studier, men ett krav om du
        genomför en scanning.
      </p>
    </>
  ),
});

const MoreInfo = localize({
  en_GB: "More information",
  sv_SE: "Mer information",
});

const Study = {
  Cardio: {
    Head: localize({
      en_GB: "Cardio Alpha",
      sv_SE: "Cardio Alpha",
    }),
    Description: localize({
      en_GB: (
        <>
          <p>
            Arterial stiffness is a sign of ageing of the cardiovascular system,
            and it is strongly associated with hypertension and increased risk
            for cardiovascular disease.
          </p>
          <p>
            This clinical trial is about evaluating the suitability of a novel
            Laser-Radar-based technology, to assess arterial stiffness and its
            correlation to cardiovascular risk and diseases.
          </p>
        </>
      ),
      sv_SE: (
        <>
          <p>
            Arteriell stelhet är ett tecken på åldrande av det kardiovaskulära
            systemet, och det är starkt förknippat med högt blodtryck och ökad
            risk för hjärt-kärlsjukdom.
          </p>
          <p>
            Denna kliniska prövning handlar om att utvärdera lämpligheten av en
            ny laser-radarbaserad teknologi, för att bedöma arteriell stelhet
            och dess korrelation till kardiovaskulär risk och sjukdomar.
          </p>
        </>
      ),
    }),
  },
  Spectrum: {
    Head: localize({
      en_GB: "Spectrum",
      sv_SE: "Spectrum",
    }),
    Description: localize({
      en_GB: (
        <>
          <p>
            Most skin conditions affect and/or are caused by the skin's
            microcirculation (blood flow in the smallest vessels).
          </p>
          <p>
            Microcirculation in the skin is also affected by many systemic
            diseases, such as for example, diabetes and autoimmune diseases.
          </p>
          <p>
            The disease of large vessels (macro-circulation) can also be caused
            by basic problems with microcirculation.
          </p>
          <p>
            Today, there is little opportunity to detect microcirculatory
            problems early, and it is often discovered when the course of the
            systemic disease has gone quite far.
          </p>
          <p>
            This clinical trial will evaluate a trial device that uses a
            projector to depict patterns on the skin, which the skin absorbs,
            reflects, and spreads depending on skin composition and structure.
          </p>
        </>
      ),
      sv_SE: (
        <>
          <p>
            De flesta hudåkommor påverkar och/eller orsakas av hudens
            mikrocirkulation (blodflöde i de minsta kärlen). Mikrocirkulationen
            i huden påverkas också av många systemsjukdomar, som till exempel
            diabetes och autoimmuna sjukdomar.
          </p>
          <p>
            Sjukdomar i stora kärl (makrocirkulation) kan också orsakas av
            grundläggande problem med mikrocirkulationen.
          </p>
          <p>
            Idag finns få möjligheter att tidigt upptäcka
            mikrocirkulationsproblem och de upptäcks ofta när förloppet av den
            systemiska sjukdomen har gått ganska långt.
          </p>
          <p>
            Denna kliniska prövning kommer att utvärdera en studiedevice som
            använder en projektor för att avbilda ett ljusmönster på huden, som
            huden absorberar, reflekterar och sprider beroende på hudens
            sammansättning och struktur.
          </p>
          <p>
            Denna kliniska prövning kommer att utvärdera en studiedevice som
            använder en projektor för att avbilda ett ljusmönster på huden, som
            huden absorberar, reflekterar och sprider beroende på hudens
            sammansättning och struktur.
          </p>
        </>
      ),
    }),
  },
  Derma: {
    Head: localize({
      en_GB: "DermaFlow Alpha",
      sv_SE: "DermaFlow Alpha",
    }),
    Description: localize({
      en_GB: (
        <>
          <p>
            The skin is our biggest organ that changes all the time and varies a
            lot from person to person. The skin often gives us clues about what
            is happening in other parts of the body.
          </p>
          <p>
            This clinical trial is about evaluating the suitability of
            multi-modal imaging technology that virtually covers the entire
            body, to assess the potential of screening and early detection of
            disease.
          </p>
          <p>
            This clinical trial is about evaluating the suitability of
            multi-modal imaging technology that virtually covers the entire
            body, to assess the potential of screening and early detection of
            disease.
          </p>
          <p>
            The multi-modal technology is based on camera systems, which can
            capture high-resolution color images (visible spectrum), 3D, and
            thermal images (infrared spectrum).
          </p>
          <p>
            By studying the correlation and sensitivity between disease states,
            gradients, and asymmetries in collected image data, the potential
            can be evaluated.
          </p>
        </>
      ),
      sv_SE: (
        <>
          <p>
            Huden är vårt största organ som förändras hela tiden och varierar
            mycket från person till person. Huden ger oss ofta ledtrådar om vad
            som händer i andra delar av kroppen.
          </p>
          <p>
            Denna kliniska prövning handlar om att utvärdera lämpligheten av
            multimodal bildteknik som praktiskt taget täcker hela kroppen, för
            att bedöma potentialen för screening och tidig upptäckt av sjukdom.
          </p>
          <p>
            Den multimodala tekniken är baserad på kamerasystem, som kan fånga
            högupplösta färgbilder (synligt spektrum), 3D och värmebilder
            (infrarött spektrum).
          </p>
          <p>
            Genom att studera korrelation och känslighet mellan
            sjukdomstillstånd, gradienter och asymmetrier i insamlad bilddata
            kan potentialen utvärderas.
          </p>
        </>
      ),
    }),
  },
};

export const Trans = {
  Title,
  Summary,
  HowItWorks,
  StudyExplanation,
  Study,
  MoreInfo,
};
